import { BaseballLineupPosition, baseballLogicService, IApiLineupPlayer } from '@trackman/baseball-shared-components';
import { BroadcastItems } from 'enums/BroadcastItems';
import { Ruleset } from 'enums/Ruleset';
import { IBroadcastBase } from 'interfaces/broadcast/IBroadcastBase';
import { ITaggingProvider } from 'interfaces/providers/ITaggingProvider';
import { IApiPlayWithBallFinals } from 'interfaces/session/IApiPlayWithBallFinals';
import { IInning } from 'interfaces/tagging/IInning';
import { ApiLineupDraggablePlayer } from 'interfaces/team/ApiLineupDraggablePlayer';
import { IPlayer } from 'interfaces/team/IPlayer';
import { action, computed, IObservableArray, IReactionDisposer, makeAutoObservable, observable, reaction, runInAction, toJS } from 'mobx';
import { createTransformer } from 'mobx-utils';
import { InningManager } from 'models/tagging/InningManager';
import { Play } from 'models/tagging/Play';
import { PlayLineup } from 'models/tagging/PlayLineup';
import { serialize } from 'serializr';
import { PlayerService } from 'services/data-services/PlayerService';
import { FeatureConfigService } from 'services/FeatureConfigService';
import { getBaseRunnersConsequences } from 'services/helper-services/baseRunnersLogicHelper';
import mappingHelpers from 'services/helper-services/mappingHelpers/mappingHelpers';
import { playerToApiLineupPlayerMap } from 'services/helper-services/mappingHelpers/playerMappings';
import {
  createPlayBasedOnReferencePlay,
  recalculateComputedPlayProperties,
  setDefensivePlayersData,
  setPitcherAndBatterData,
  updatePlayWithBroadcastValues,
} from 'services/helper-services/playManipulationHelpers';
import NoteStore from 'stores/NoteStore';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import uuid from 'uuid';
import type { ChallengeCall, ChallengeResult, ChallengeTeam } from '../../interfaces/tagging/ApiModels/IChallengeTag';
import type { IStrikeZoneDecisionModel } from '../../interfaces/tagging/ApiModels/IStrikeZoneDecision';
import { LogService } from '../../services/data-services/LogService';
import { getNextInning, getPlayConsequences, getShouldProgressHalfInning } from '../../services/helper-services/baseballLogicHelpers';
import { updatePlayWithStrikeZoneDecision } from '../../services/helper-services/playManipulationHelpers';
import { ExtraInningRulesStore } from './ExtraInningRulesStore';
import { TaggingStore } from './TaggingStore';

const MINIMUM_EXTRA_INNING_NUMBER = 10;

export class TaggingPlayStore {
  constructor(
    taggingStore: TaggingStore,
    sessionStore: SessionStore,
    taggingService: ITaggingProvider,
    logService: LogService,
    extraInningRulesStore: ExtraInningRulesStore,
    playerService: PlayerService,
    noteStore: NoteStore,
    featureConfigService: FeatureConfigService,
    notificationStore: NotificationStore
  ) {
    this.taggingStore = taggingStore;
    this.taggingService = taggingService;
    this.logService = logService;
    this.sessionStore = sessionStore;
    this.extraInningRulesStore = extraInningRulesStore;
    this.inningManager = new InningManager();
    this.playerService = playerService;
    this.inningManager.increaseHalfInning();
    this.placeholderPlay = new Play(uuid());
    this.noteStore = noteStore;
    this.featureConfigService = featureConfigService;
    this.notificationStore = notificationStore;
    makeAutoObservable(this);
  }

  notificationStore: NotificationStore;

  manuallyChangedPlayerAtBat: IApiLineupPlayer | undefined;

  noteStore: NoteStore;

  extraInningRulesStore: ExtraInningRulesStore;

  sessionStore: SessionStore;

  taggingStore: TaggingStore;

  logService: LogService;

  taggingService: ITaggingProvider;

  inningManager: InningManager;

  playerService: PlayerService;

  featureConfigService: FeatureConfigService;

  @observable increaseInningRequest = false;

  @observable decraseInningRequest = false;

  @observable increaseInningRequestorId: string | undefined;

  @observable isAdvanceBatterModalVisible = false;

  @observable overrideBatterAdvancementForNextPlay = false;

  @observable placeholderPlay: Play;

  @observable selectedPlay?: Play;

  @observable playCount = 0;

  @observable selectedPlayObjectId = '';

  @observable lastStrikeZoneDecision?: IStrikeZoneDecisionModel;

  @observable isChallengeModalVisible = false;

  @observable challengeTargetPlay?: Play;

  reactionDisposer?: IReactionDisposer;

  @computed get isTopInning(): boolean {
    return this.selectedPlay ? this.selectedPlay.isTopInning : true;
  }

  @computed get isPlaceholderPlaySelected(): boolean | undefined {
    return this.selectedPlay && this.selectedPlay.isPlaceholder;
  }

  @computed get getDisplayPlaysForInning() {
    return createTransformer((inningNumber: number) => {
      if (inningNumber === 0) {
        return [this.placeholderPlay, ...this.inningManager.getInning(0).playsReversed];
      }
      return this.inningManager.getInning(inningNumber).playsReversed;
    });
  }

  @computed get shouldDisplayPlaceholder() {
    const play = this.inningManager.getLastPlay(true);
    return this.sessionStore.isPlaying && (!play || play.isTagStored);
  }

  @action removeStoredNote = (play: Play) => {
    if (play.note.includes(`${this.noteStore.note} - `)) {
      play.note = play.note.split(' - ').at(-1) || '';
    }
  };

  @action convertPlayToWarmup = async (play: Play, inningIndex: number) => {
    const inning = this.inningManager.getInning(inningIndex - 1);
    const playToSelect = this.getPlayToSelectAfterConversion(inning, play);
    play.isWarmup = true;
    this.playCount--;
    this.removeStoredNote(play);

    await this.storeTag();
    this.selectedPlay = playToSelect;
  };

  getPlayToSelectAfterConversion = (inning: IInning, convertedPlay: Play): Play | undefined => {
    if (this.playCount === 1) {
      return undefined;
    }
    if (this.inningManager.getLastPlay(true)!.id === convertedPlay.id) {
      return this.inningManager.getPreviousLivePlay(convertedPlay);
    }
    return this.inningManager.getNextLivePlay(convertedPlay);
  };

  @action initializeState = (plays: IApiPlayWithBallFinals[]) => {
    this.inningManager.inningsReversed = mappingHelpers.apiPlaysToInningsMap(plays);
    this.playCount = plays.filter((x) => !x.play.taggerBehavior.isWarmup).length;
    this.selectedPlay = this.playCount > 0 ? this.inningManager.getLastLivePlay(true) : undefined;
    if (this.inningManager.inningsReversed.length === 0) {
      this.inningManager.increaseHalfInning();
    }
    const newestInning = this.inningManager.getInning(0);
    if (this.selectedPlay) {
      if (this.selectedPlay.inningNumber === newestInning.number && this.selectedPlay.isTopInning === newestInning.isTop) {
        const consequences = getPlayConsequences(this.selectedPlay);
        if (consequences.shouldProgressHalfInning) {
          this.inningManager.increaseHalfInning(this.selectedPlay.id);
        }
      } else {
        this.inningManager.playObjectIdThatTriggeredLastInningProgression = this.selectedPlay.id;
      }
    }

    this.recreatePlaceholderPlay();
    this.cleanUpReaction();
    this.reactionDisposer = reaction(
      () => this.selectedPlay && serialize(this.selectedPlay),
      (data) => {
        if (data) {
          if (this.selectedPlayObjectId === data.id) {
            // update
            this.selectedPlay!.isChanged = true;
          } else {
            // just clicked on different play
            this.selectedPlayObjectId = data.id;
          }
        }
      }
    );
  };

  @action addPlay = (play: Play, inningIndex = 0, playIndex = 0) => {
    this.inningManager.getInning(inningIndex).playsReversed.splice(playIndex, 0, play);

    if (!play.isWarmup) {
      this.playCount++;
    }
    return this.inningManager.getPlay(inningIndex, playIndex);
  };

  @action private createAndAddNewPlayOnTopOfTheList = async (playId: string, isCreatedManually = false) => {
    const play = this.createNextPlay(playId, isCreatedManually, this.manuallyChangedPlayerAtBat);
    this.overrideBatterAdvancementForNextPlay = false;
    const shouldSelect = play.isLive && (!this.selectedPlay || this.selectedPlay.isPlaceholder || isCreatedManually);
    const newPlay = this.addPlay(play);
    if (shouldSelect) {
      this.selectedPlay = newPlay;
    }
    // don't await on purpose - this is the automatic save and even if it failed we wouldn't do anything
    if (isCreatedManually) {
      this.taggingService.storePlay(
        mappingHelpers.playToApiPlayModelMap(newPlay, this.sessionStore.currentSessionMetadata),
        'TaggingPlayStore@createAndAddNewPlayOnTopOfTheList'
      );
    }
    newPlay.isChanged = false;

    return newPlay;
  };

  @action addNoteToFirstPlay = () => {
    if (this.noteStore.note.length > 0) {
      const firstLivePlay = this.inningManager.getFirstLivePlay();

      if (firstLivePlay && !firstLivePlay.note.includes(`${this.noteStore.note + ' - '}`)) {
        firstLivePlay.note = `${this.noteStore.note}${firstLivePlay.note && ` - ${firstLivePlay.note}`}`;
      }
    }
  };

  @action recalculateComputedProperties = async () => {
    const updatedPlays = recalculateComputedPlayProperties(this.inningManager.inningsReversed, this.sessionStore.currentSessionMetadata);

    this.addNoteToFirstPlay();

    try {
      if (updatedPlays.length > 0) {
        await this.taggingService.storePlays(
          updatedPlays.map((p) => mappingHelpers.playToApiPlayModelMap(p, this.sessionStore.currentSessionMetadata)),
          'TaggingPlayStore@recalculateComputedProperties'
        );
      }
      return updatedPlays;
    } catch (err: unknown) {
      this.logService.logError(err, {
        function: 'TaggingPlayStore@recalculateComputedProperties',
      });
      if (err instanceof Error) {
        this.notificationStore.addNotification({
          id: 'recalculate-computed-properties-error',
          message: err.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('recalculate-computed-properties-error');
          },
        });
      }
    }
    return [];
  };

  @action recreatePlaceholderPlay = () => {
    const play = this.createNextPlay(uuid(), false, this.manuallyChangedPlayerAtBat);
    play.isPlaceholder = true;
    if (this.selectedPlay === this.placeholderPlay) {
      this.selectedPlay = play;
    }
    this.placeholderPlay = play;
  };

  @action createManualPlayOnTopOfTheList = () => {
    return this.createAndAddNewPlayOnTopOfTheList(uuid(), true);
  };

  @action createManualPlayAfterSelected = () => {
    return this.createAndAddNewPlayWithCustomPosition(this.selectedPlay!, false);
  };

  @action createManualPlayBeforeSelected = () => {
    return this.createAndAddNewPlayWithCustomPosition(this.selectedPlay!, true);
  };

  // This won't store updated values untill tagger hits save (maybe refactor)
  @action createOrUpdatePlayFromBroadcast = async (broadcastItem: IBroadcastBase, broadcastItemType: BroadcastItems) => {
    let existingPlay = this.inningManager.getPlayByPlayId(broadcastItem.playId);
    if (!existingPlay) {
      existingPlay = await this.createAndAddNewPlayOnTopOfTheList(broadcastItem.playId);
      this.lastStrikeZoneDecision &&
        this.lastStrikeZoneDecision.playId === existingPlay.playId &&
        updatePlayWithStrikeZoneDecision(existingPlay, this.lastStrikeZoneDecision);
    }
    const isRoboUmpEnabled = this.featureConfigService.getConfig().isRoboUmpEnabled;
    runInAction(() =>
      updatePlayWithBroadcastValues(
        existingPlay!,
        broadcastItem,
        broadcastItemType,
        isRoboUmpEnabled,
        this.sessionStore.currentSessionMetadata.strikeZone
      )
    );

    await this.taggingService.storePlay(
      existingPlay.toApiModel(this.sessionStore.currentSessionMetadata),
      'TaggingPlayStore@createOrUpdatePlayFromBroadcast'
    );
  };

  @action openChallengeModalForPlay = (play: Play) => {
    this.challengeTargetPlay = play;
    this.isChallengeModalVisible = true;
  };

  @action closeChallengeModal = () => {
    this.isChallengeModalVisible = false;
    this.challengeTargetPlay = undefined;
  };

  @action setChallengeTag = async (team: ChallengeTeam, call: ChallengeCall, result: ChallengeResult) => {
    const play = this.challengeTargetPlay;
    if (!play) return;
    const isEdit = !!play.challengeTag;
    const pitchNumber = play.playSequenceNumber;
    play.challengeTag = {
      challengedUtc: new Date().toISOString(),
      challengeType: 'PitchCall',
      challengeTeam: team,
      challengeCall: call,
      challengeResult: result,
    };
    this.closeChallengeModal();
    this.notificationStore.addToast({
      header: 'Challenge saved',
      message: isEdit ? `A challenge has been saved to pitch ${pitchNumber}.` : `ABS Challenge saved to pitch ${pitchNumber}.`,
      severity: 'success',
      open: true,
      onClose: () => this.notificationStore.removeToast(),
    });
    await this.taggingService.storePlay(play.toApiModel(this.sessionStore.currentSessionMetadata), 'TaggingPlayStore@setChallengeTag');
  };

  @action removeChallengeTag = async () => {
    const play = this.challengeTargetPlay;
    if (!play) return;
    const pitchNumber = play.playSequenceNumber;
    play.challengeTag = undefined;
    this.closeChallengeModal();
    this.notificationStore.addToast({
      header: 'Challenge removed',
      message: `ABS Challenge removed from pitch ${pitchNumber}.`,
      severity: 'success',
      open: true,
      onClose: () => this.notificationStore.removeToast(),
    });
    await this.taggingService.storePlay(play.toApiModel(this.sessionStore.currentSessionMetadata), 'TaggingPlayStore@removeChallengeTag');
  };

  @action handleStrikeZoneDecision = async (decision: IStrikeZoneDecisionModel) => {
    this.lastStrikeZoneDecision = decision;
    const existingPlay = this.inningManager.getPlayByPlayId(decision.playId);
    if (existingPlay) {
      runInAction(() => updatePlayWithStrikeZoneDecision(existingPlay!, decision));
    }
  };

  @action storeTag = async () => {
    if (!this.selectedPlay || this.selectedPlay.isPlaceholder) {
      return;
    }
    const consequence = getPlayConsequences(this.selectedPlay);
    this.selectedPlay.korbb = consequence.isKOrBb;
    this.selectedPlay.isValidated = true;

    this.taggingStore.isLoading = true;

    if (
      this.sessionStore.currentSessionMetadata.isPlayerTrackingEnabled &&
      !this.selectedPlay.isBRChangedManually &&
      !this.selectedPlay.isBaseRunnersLogicExecuted
    ) {
      this.selectedPlay.baseRunners = getBaseRunnersConsequences(this.selectedPlay);
      this.selectedPlay.isBaseRunnersLogicExecuted = true;
    }

    try {
      const updatedPlays = await this.recalculateComputedProperties();
      if (!updatedPlays.some((p) => p.id === this.selectedPlay?.id)) {
        // if play has already been stored by recalculateComputedProperties, no need to do it again
        await this.taggingService.storePlay(
          this.selectedPlay.toApiModel(this.sessionStore.currentSessionMetadata),
          'TaggingPlayStore@storeTag'
        );
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        this.notificationStore.addNotification({
          id: 'store-tag-error',
          message: err.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('store-tag-error');
          },
        });
      }
    }

    this.taggingStore.isLoading = false;
    this.selectedPlay.isTagStored = true;
    this.selectedPlay.isChanged = false;
    this.selectedPlay.shouldAnimate = true;

    const lastPlay = this.inningManager.getLastPlay(true);
    // only if we're updating latest play. updating historical plays doesn't trigger any inning progression logic
    if (lastPlay && this.selectedPlay?.id === lastPlay?.id) {
      const shouldProgressHalfInning = getShouldProgressHalfInning(this.selectedPlay);
      if (shouldProgressHalfInning && this.inningManager.playObjectIdThatTriggeredLastInningProgression !== this.selectedPlay!.id) {
        // if half-inning should be progressed and hasn't been already
        this.setIncreaseInningRequest(true, this.selectedPlay?.id);
      } else if (this.sessionStore.currentSessionMetadata.ruleSet === Ruleset.scrimmage && consequence.shouldProgressBatter) {
        this.isAdvanceBatterModalVisible = true;
      }
      this.recreatePlaceholderPlay();
      if (this.shouldDisplayPlaceholder) {
        this.selectedPlay = this.placeholderPlay;
      }
    }
  };

  @action updateBallsAndStrikes = () => {
    this.selectedPlay!.balls.fromTag = baseballLogicService.isBall(this.selectedPlay!.tag.pitchCall) ? 1 : 0;
    this.selectedPlay!.strikes.fromTag = baseballLogicService.isStrike(this.selectedPlay!.tag.pitchCall, this.selectedPlay!.strikes.initial)
      ? 1
      : 0;
  };

  @action setIncreaseInningRequest = (request: boolean, requestorId?: string | undefined) => {
    this.increaseInningRequest = request;
    this.increaseInningRequestorId = requestorId;
  };

  @action setDecreaseInningRequest = (request: boolean) => {
    this.decraseInningRequest = request;
  };

  @action increaseInning = () => {
    const newestInning = this.inningManager.getInning(0);
    const previousInning = this.inningManager.inningsReversed.length > 1 ? this.inningManager.getInning(1) : undefined;
    const inningToPush = getNextInning(newestInning.number, newestInning.isTop, true);
    if (
      newestInning.playsReversed.some((p) => p.isLive) ||
      !previousInning ||
      previousInning.number !== inningToPush.number ||
      previousInning.isTop !== inningToPush.isTop
    ) {
      this.inningManager.increaseHalfInning(this.increaseInningRequestorId);
    } else {
      this.inningManager.removeLastHalfInning();
    }
    if (this.increaseInningRequest) {
      this.setIncreaseInningRequest(false);
    }
    this.recreatePlaceholderPlay();
  };

  @action decreaseInning = () => {
    const newestInning = this.inningManager.getInning(0);
    const previousInning = this.inningManager.inningsReversed.length > 1 ? this.inningManager.getInning(1) : undefined;
    const inningToPush = getNextInning(newestInning.number, newestInning.isTop, false);
    if (
      newestInning.playsReversed.some((p) => p.isLive) ||
      !previousInning ||
      previousInning.number !== inningToPush.number ||
      previousInning.isTop !== inningToPush.isTop
    ) {
      this.inningManager.decreaseHalfInning();
    } else {
      this.inningManager.removeLastHalfInning();
    }
    if (this.decraseInningRequest) {
      this.setDecreaseInningRequest(false);
    }
    this.recreatePlaceholderPlay();
  };

  @action promptInningChange = (changeInning: boolean) => {
    return changeInning;
  };

  @action private createAndAddNewPlayWithCustomPosition = async (referencePlay: Play, insertBeforeReference: boolean) => {
    const { playStore } = this.taggingStore;
    const { getPlay } = playStore.inningManager;

    const play = createPlayBasedOnReferencePlay(referencePlay, insertBeforeReference);
    play.isCreatedManually = true;
    play.isChanged = true;
    const { inningIndex, playIndex } = this.inningManager.getPlayDataById(referencePlay.id);
    const firstPlayInInningIndex = playStore.inningManager.inningsReversed[inningIndex!].playsReversed.length - 1;
    const newIndex = insertBeforeReference ? playIndex! + 1 : playIndex!;
    const playToGetLineupFrom = getPlay(inningIndex!, newIndex);
    play.playLineup =
      playToGetLineupFrom === undefined ? getPlay(inningIndex!, firstPlayInInningIndex).playLineup : playToGetLineupFrom.playLineup;

    const newPlay = this.addPlay(play, inningIndex!, newIndex);
    this.selectedPlay = newPlay;
    await this.recalculateComputedProperties();
    return newPlay;
  };

  @action private createNextPlay = (
    playId: string,
    isCreatedManually: boolean,
    manuallyChangedPlayerAtBat: IApiLineupPlayer | undefined
  ) => {
    const play = new Play(playId);
    play.playLineup = new PlayLineup(
      this.sessionStore.currentSessionMetadata.homeTeam?.lineup ?? [],
      this.sessionStore.currentSessionMetadata.awayTeam?.lineup ?? []
    );
    play.isCreatedManually = isCreatedManually;
    play.isChanged = true;
    play.inningNumber = this.inningManager.getInning(0).number;
    play.isTopInning = this.inningManager.getInning(0).isTop;
    if (!isCreatedManually && !this.sessionStore.isPlaying) {
      play.isWarmup = true;
    }
    const lastPlay = this.inningManager.getLastLivePlay(false);
    const isPlayInCurrentInning = (play: Play) =>
      play.inningNumber === this.inningManager.getInning(0).number && play.isTopInning === this.inningManager.getInning(0).isTop;

    setDefensivePlayersData(play);

    if (lastPlay) {
      play.sequenceNumber = lastPlay.sequenceNumber + 1;
      play.homeTeamScore.initial = toJS(lastPlay.homeTeamScore.total);
      play.awayTeamScore.initial = toJS(lastPlay.awayTeamScore.total);
      let consequence = getPlayConsequences(lastPlay, !isPlayInCurrentInning(lastPlay));

      if (isPlayInCurrentInning(lastPlay)) {
        play.balls.initial = consequence.nextPlayBalls;
        play.strikes.initial = consequence.nextPlayStrikes;
        play.outs.initial = consequence.nextPlayOuts;
        setPitcherAndBatterData(play, lastPlay, !this.overrideBatterAdvancementForNextPlay && consequence.shouldProgressBatter, undefined);

        play.baseRunners = lastPlay.baseRunners;
      } else {
        const lastPlayFromPrevious = this.inningManager.getLastPlayFromPreviousInning(play.inningNumber, play.isTopInning);
        if (lastPlayFromPrevious) {
          consequence = getPlayConsequences(lastPlayFromPrevious, true);

          play.baseRunners = {
            firstBase: null,
            secondBase: null,
            thirdBase: null,
          };

          if (this.extraInningRulesStore.isExtraRunnerEnabled) {
            const isFirstPlayInInning = this.inningManager.isFirstPlayInInning();

            if (this.inningManager.getLatestInningNumber() >= MINIMUM_EXTRA_INNING_NUMBER && isFirstPlayInInning) {
              play.baseRunners = this.extraInningRulesStore.addExtraRunner(lastPlayFromPrevious);
            }
          }
        }

        setPitcherAndBatterData(
          play,
          lastPlayFromPrevious,
          !this.overrideBatterAdvancementForNextPlay && consequence.shouldProgressBatter,
          manuallyChangedPlayerAtBat
        );
      }
    } else {
      play.sequenceNumber = 1;
      play.inningNumber = this.inningManager.getInning(0).number;
      play.isTopInning = this.inningManager.getInning(0).isTop;
      setPitcherAndBatterData(play, undefined, false, manuallyChangedPlayerAtBat);
    }

    const batter = play
      .getBattingTeamLineup()
      .getBatterList()
      .find((player) => player.id === play.batter.id);

    if (batter) {
      try {
        this.playerService.savePlayerAtBat(batter);
      } catch {}
    }

    if (!play.isWarmup) {
      play.playSequenceNumber = this.playCount + 1;
    }

    this.manuallyChangedPlayerAtBat = undefined;

    return play;
  };

  @action manuallyChangePlayerAtBat = (player: IApiLineupPlayer) => {
    this.playerService.savePlayerAtBat(player);
    this.manuallyChangedPlayerAtBat = player;
  };

  @action updateLineup = async (
    play: Play,
    isBattingLineup: boolean,
    newLineup: ApiLineupDraggablePlayer[],
    oldLineup?: IApiLineupPlayer[]
  ) => {
    const lineup = isBattingLineup ? play.getBattingTeamLineup() : play.getPitchingTeamLineup();

    // Update batting order
    newLineup.forEach((entry, index) => {
      entry.battingOrder = index;
    });

    if (newLineup.length < lineup.players.length && !newLineup.some((p) => p.position === BaseballLineupPosition.Pitcher)) {
      // pitcher is not batting
      newLineup.push(lineup.getPlayer(BaseballLineupPosition.Pitcher));
    }
    if (isBattingLineup) {
      const currentBatter = lineup.players.find((p) => p.id === play.batter.id);
      const order = currentBatter?.battingOrder;
      const newBatter = newLineup.find((p) => p.battingOrder === order);
      if (newBatter) {
        play.batter = newBatter;
      }
    }

    (lineup.players as IObservableArray<ApiLineupDraggablePlayer>).replace(newLineup);
    const metadata = { ...this.sessionStore.currentSessionMetadata };
    if (play.isHomeTeamBatting === isBattingLineup) {
      if (metadata.homeTeam) {
        metadata.homeTeam = { ...metadata.homeTeam, lineup: newLineup };
      }
    } else if (metadata.awayTeam) {
      metadata.awayTeam = { ...metadata.awayTeam, lineup: newLineup };
    }

    if (!isBattingLineup && !lineup.isPlayerInLineup(play.pitcher.id)) {
      play.pitcher = lineup.getPlayer(BaseballLineupPosition.Pitcher);
    }
    play.isChanged = true;

    play.firstBaseman = play.getPitchingTeamLineup().players.find((p) => p.position === BaseballLineupPosition.FirstBase) || null;
    play.secondBaseman = play.getPitchingTeamLineup().players.find((p) => p.position === BaseballLineupPosition.SecondBase) || null;
    play.thirdBaseman = play.getPitchingTeamLineup().players.find((p) => p.position === BaseballLineupPosition.ThirdBase) || null;
    play.centerFielder = play.getPitchingTeamLineup().players.find((p) => p.position === BaseballLineupPosition.CenterField) || null;
    play.leftFielder = play.getPitchingTeamLineup().players.find((p) => p.position === BaseballLineupPosition.LeftField) || null;
    play.rightFielder = play.getPitchingTeamLineup().players.find((p) => p.position === BaseballLineupPosition.RightField) || null;
    play.shortStopper = play.getPitchingTeamLineup().players.find((p) => p.position === BaseballLineupPosition.Shortstop) || null;

    if (isBattingLineup && oldLineup) {
      this.handleMultipleBaserunnerSubstitution(play, oldLineup, newLineup);
    }

    if (play.isPlaceholder) {
      this.selectedPlay = play;
      if (play.batter) {
        this.playerService.savePlayerAtBat(play.batter as IApiLineupPlayer);
      }
    }

    if (this.inningManager.getLastPlay(true)?.id === play.id || play.isPlaceholder) {
      await this.sessionStore.updateSessionMetadata(metadata, false);
    }
  };

  @action updateLineupOrder = async (play: Play, newLineup: ApiLineupDraggablePlayer[]) => {
    await this.updateLineup(play, true, newLineup);
  };

  @action substitutePlayer = async (play: Play, oldPlayerId: string, newPlayer: IPlayer, isPitcher: boolean) => {
    if (play.isPlaceholder && !this.inningManager.isFirstPlayInInning()) {
      return;
    }

    const lineup = isPitcher ? play.getPitchingTeamLineup() : play.getBattingTeamLineup();
    const currentLineup = [...lineup.players];
    const i = currentLineup.findIndex((player) => player.id === oldPlayerId);
    const newEntry = playerToApiLineupPlayerMap(newPlayer, currentLineup[i].position, currentLineup[i].battingOrder);
    currentLineup.splice(i, 1, newEntry);
    this.handleBaserunnerSubstitution(play, oldPlayerId, newEntry);
    await this.updateLineup(play, !isPitcher, currentLineup);
  };

  handleBaserunnerSubstitution = (play: Play, oldPlayerId: string, newPlayer: ApiLineupDraggablePlayer) => {
    if (play.baseRunners.firstBase?.id === oldPlayerId) {
      play.baseRunners.firstBase = newPlayer;
    } else if (play.baseRunners.secondBase?.id === oldPlayerId) {
      play.baseRunners.secondBase = newPlayer;
    } else if (play.baseRunners.thirdBase?.id === oldPlayerId) {
      play.baseRunners.thirdBase = newPlayer;
    }
  };

  handleMultipleBaserunnerSubstitution = (play: Play, oldLineup: IApiLineupPlayer[], newLineup: IApiLineupPlayer[]) => {
    oldLineup.forEach((player) => {
      if (player.id === play.baseRunners.firstBase?.id) {
        play.baseRunners.firstBase = newLineup.find((newPlayer) => newPlayer.position === play.baseRunners.firstBase?.position) || null;
      } else if (player.id === play.baseRunners.secondBase?.id) {
        play.baseRunners.secondBase = newLineup.find((newPlayer) => newPlayer.position === play.baseRunners.secondBase?.position) || null;
      } else if (player.id === play.baseRunners.thirdBase?.id) {
        play.baseRunners.thirdBase = newLineup.find((newPlayer) => newPlayer.position === play.baseRunners.thirdBase?.position) || null;
      }
    });
  };

  cleanUpReaction = () => {
    if (this.reactionDisposer) {
      this.reactionDisposer();
    }
  };

  dispose = () => {
    this.cleanUpReaction();
  };
}
